import { Alert, Button, Empty, Skeleton, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { LeadScheduleEditorModal } from "../schedule/LeadScheduleEditorModal.tsx"
import { LEAD_WORKSPACE_TAB_PARAM } from "../use-lead-workspace-tab.ts"
import { type Lead } from "../../types/lead.types.ts"
import { EducationEmptyState } from "./EducationEmptyState.tsx"
import { EducationTrackView } from "./EducationTrackView.tsx"
import { ReviewInputsDrawer } from "./ReviewInputsDrawer.tsx"
import { useLeadEducation } from "./hooks/use-lead-education.ts"
import type { Audience } from "./types/education.types.ts"

const { Title } = Typography

interface LeadEducationTabProps {
  leadId: string
  canEdit: boolean
  /** Only given by the real workspace tab registry — a bare unit test that
   *  mounts this tab with just `leadId`/`canEdit` gets no "Invite to
   *  meeting" button rather than a crash, since `LeadScheduleEditorModal`
   *  reaches into Redux for the signed-in user's timezone. */
  lead?: Lead
  owners?: BaseOptionType[]
  saving?: boolean
  onPatch?: LeadPatchSender
}

/**
 * Registered as the "Project Education" workspace tab. Reads `?audience=`
 * itself (the backend's meeting-invite deep link carries it) rather than
 * going through `onTabChange`/`LeadWorkspaceTabContext`, since the tab
 * registration (`lead-workspace-tabs.tsx`) only ever hands this component
 * `leadId`/`canEdit` — everything else it needs comes from its own
 * `useLeadEducation` fetch or the URL.
 */
export function LeadEducationTab({
  leadId,
  canEdit,
  lead,
  owners,
  saving,
  onPatch
}: LeadEducationTabProps) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedAudience = searchParams.get("audience") as Audience | null
  const [reviewOpen, setReviewOpen] = useState(false)
  const [scheduling, setScheduling] = useState(false)

  const goToTab = useCallback(
    (key: string) => {
      setSearchParams(
        (prev) => {
          prev.set(LEAD_WORKSPACE_TAB_PARAM, key)
          return prev
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data, loading, error, refresh, generate, retry, askMore, publish, approve } =
    useLeadEducation(leadId)

  if (loading && !data) {
    return <Skeleton active data-testid={"education-tab-loading"} />
  }

  if (!data) {
    return (
      <Alert
        type={"error"}
        showIcon
        message={t("projectEducation.error.loadTitle")}
        description={error?.message ?? t("projectEducation.error.network")}
        action={
          <Button size={"small"} onClick={() => void refresh()}>
            {t("projectEducation.error.retry")}
          </Button>
        }
      />
    )
  }

  if (!data.enabled) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("projectEducation.disabled")}
      />
    )
  }

  const canInviteToMeeting = canEdit && !!lead && !!onPatch

  return (
    <div data-testid={"lead-education-tab"}>
      {error && (
        <Alert
          className={"mb-3"}
          type={"warning"}
          showIcon
          data-testid={"education-refresh-failed"}
          message={t("projectEducation.error.refreshFailed")}
          action={
            <Button size={"small"} onClick={() => void refresh()}>
              {t("projectEducation.error.retry")}
            </Button>
          }
        />
      )}
      {data.package ? (
        <div className={"flex flex-col gap-4"}>
          <Title level={4} className={"m-0"}>
            {t("projectEducation.header.title")}
          </Title>
          <EducationTrackView
            canEdit={canEdit}
            flags={data.flags}
            permissions={data.permissions}
            packageView={data.package}
            inFlightPackage={data.in_flight_package}
            requestedAudience={requestedAudience}
            onReviewInputs={() => setReviewOpen(true)}
            onGenerate={generate}
            onRetry={retry}
            onAskMore={askMore}
            onInviteToMeeting={canInviteToMeeting ? () => setScheduling(true) : undefined}
            onEditionViewed={refresh}
            onPublish={publish}
            onApprove={approve}
            onGoToTab={goToTab}
            preparedFor={lead?.customer?.first_name || null}
            sharedBy={lead?.sales_manager_name ?? null}
          />
        </div>
      ) : (
        <EducationEmptyState
          inputs={data.inputs}
          flags={data.flags}
          permissions={data.permissions}
          canEdit={canEdit}
          onGenerate={generate}
          onGoToTab={goToTab}
          onReviewInputs={() => setReviewOpen(true)}
        />
      )}

      <ReviewInputsDrawer
        open={reviewOpen}
        fields={data.inputs.fields}
        onClose={() => setReviewOpen(false)}
      />

      {canInviteToMeeting && lead && onPatch && (
        <LeadScheduleEditorModal
          kind={scheduling ? "appointment" : null}
          lead={lead}
          owners={owners ?? []}
          saving={saving}
          onChange={onPatch}
          onClose={() => setScheduling(false)}
        />
      )}
    </div>
  )
}
